/* v8 ignore start */
import {JsonModel} from "@/lib/core/jsonModel";
import {LottieMultiDimensionalModel} from "@/lib/lottie/animatedProperties/lottieMultiDimensionalModel";
import {LottiePositionModel} from "@/lib/lottie/animatedProperties/lottiePositionModel";
import {LottieSplitVectorModel} from "@/lib/lottie/animatedProperties/lottieSplitVectorModel";
import {LottieValueModel} from "@/lib/lottie/animatedProperties/lottieValueModel";
import {AnimatedSegmentTransitionModel} from "@/models/animatedSegmentTransitionModel";

export class LottieTransformModel<
  T extends LottieTransformModel.IJson = LottieTransformModel.IJson,
> extends JsonModel<T> {
  public get opacity(): Maybe<LottieValueModel> {
    if (this.json.o) return new LottieValueModel(this.json.o);
  }

  public get position(): Maybe<LottiePositionModel> {
    if (!this.json.p) return;
    if (this.isPositionSplitVector(this.json.p)) throw new Error("Split vector not supported");

    return new LottiePositionModel(this.json.p);
  }

  public get anchorPoint(): Maybe<LottiePositionModel> {
    if (this.json.a) return new LottiePositionModel(this.json.a);
  }

  public get rotation(): Maybe<LottieValueModel> {
    if (this.json.r) return new LottieValueModel(this.json.r);
  }

  public get scale(): Maybe<LottieMultiDimensionalModel> {
    if (this.json.s) return new LottieMultiDimensionalModel(this.json.s);
  }

  public offsetPosition(offsetX: number, offsetY: number): this {
    if (!this.position) return this;

    return this.update({
      p: this.position.offsetPositionBy(offsetX, offsetY).toObject(),
    } as Partial<T>);
  }

  public changeOpacity(opacity: number): this {
    if (!this.opacity) return this;

    return this.update({o: this.opacity.changeValue(opacity).toObject()} as Partial<T>);
  }

  public applyFadeOut(durationFrames: number, toFrames: number, intensity: number = 20): this {
    if (!this.opacity) return this;

    const newKeyframes = [
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.167], y: [0]},
        t: toFrames - durationFrames,
        s: [100],
      },
      {t: toFrames, s: [intensity]},
    ];

    return this.update({
      o: this.opacity.forceAnimateWithKeyFrames(newKeyframes).toObject(),
    } as Partial<T>);
  }

  public applyFadeIn(durationFrames: number, fromFrames: number, intensity: number = 20): this {
    if (!this.opacity) return this;

    const newKeyframes = [
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.167], y: [0]},
        t: fromFrames,
        s: [intensity],
      },
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.333], y: [0]},
        t: fromFrames + durationFrames,
        s: [100],
      },
    ];

    return this.update({
      o: this.opacity.forceAnimateWithKeyFrames(newKeyframes).toObject(),
    } as Partial<T>);
  }

  // TODO: read previous "s" values (because it might be a different aspect ratio, width and height)
  public applyFullScreenSlideOut(
    durationFrames: number,
    toFrames: number,
    transitionType: AnimatedSegmentTransitionModel.Type
  ): this {
    if (!this.position) return this;

    let finalS = [960, 540, 0];

    // TODO: Refactor
    if (transitionType === AnimatedSegmentTransitionModel.Type.SlideLeft) {
      finalS = [960 * -1, 540, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideRight) {
      finalS = [960 * 3, 540, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideUp) {
      finalS = [960, 540 * -1, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideDown) {
      finalS = [960, 540 * 3, 0];
    }

    const newKeyframes = [
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.167], y: [0]},
        t: toFrames - durationFrames,
        s: [960, 540, 0],
      },
      {t: toFrames, s: finalS},
    ];

    return this.update({
      p: this.position.forceAnimateWithKeyFrames(newKeyframes).toObject(),
    } as Partial<T>);
  }

  // TODO: read previous "s" values (because it might be a different aspect ratio, width and height)
  public applyFullScreenSlideIn(
    durationFrames: number,
    fromFrames: number,
    transitionType: AnimatedSegmentTransitionModel.Type
  ): this {
    if (!this.position) return this;

    let initialS = [960, 540, 0];

    // TODO: Refactor
    if (transitionType === AnimatedSegmentTransitionModel.Type.SlideLeft) {
      initialS = [960 * 3, 540, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideRight) {
      initialS = [960 * -1, 540, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideUp) {
      initialS = [960, 540 * 3, 0];
    } else if (transitionType === AnimatedSegmentTransitionModel.Type.SlideDown) {
      initialS = [960, 540 * -1, 0];
    }

    const newKeyframes = [
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.167], y: [0]},
        t: fromFrames,
        s: initialS,
      },
      {
        i: {x: [0.833], y: [1]},
        o: {x: [0.333], y: [0]},
        t: fromFrames + durationFrames,
        s: [960, 540, 0],
      },
    ];

    return this.update({
      p: this.position.forceAnimateWithKeyFrames(newKeyframes).toObject(),
    } as Partial<T>);
  }

  private isPositionSplitVector(json: any): json is LottieSplitVectorModel.IJson {
    return (json as LottieSplitVectorModel.IJson).y !== undefined;
  }

  public adjustTimingBy(factor: number): this {
    return this.update({
      o: this.opacity?.adjustTimingBy(factor).toObject(),
      a: this.anchorPoint?.adjustTimingBy(factor).toObject(),
      r: this.rotation?.adjustTimingBy(factor).toObject(),
      p: this.position?.adjustTimingBy(factor).toObject(),
      s: this.scale?.adjustTimingBy(factor).toObject(),
    } as Partial<T>);
  }

  public offsetTimingsBy(offset: number): this {
    return this.update({
      o: this.opacity?.offsetTimingBy(offset).toObject(),
      a: this.anchorPoint?.offsetTimingBy(offset).toObject(),
      r: this.rotation?.offsetTimingBy(offset).toObject(),
      p: this.position?.offsetTimingBy(offset).toObject(),
      s: this.scale?.offsetTimingBy(offset).toObject(),
    } as Partial<T>);
  }
}

export namespace LottieTransformModel {
  export interface IJson {
    /**
     * Anchor point: a position (relative to its parent) around which transformations are applied (ie: center for rotation / scale)
     */
    a?: LottiePositionModel.TJson;

    /**
     * Scale factor, [100, 100] for no scaling
     */
    s?: LottieMultiDimensionalModel.TJson;

    /**
     * Opacity
     */
    o?: LottieValueModel.TJson;

    /**
     * Skew amount as an angle in degrees
     */
    sk?: LottieValueModel.TJson;

    /**
     * Direction along which skew is applied, in degrees (0 skews along the X axis, 90 along the Y axis)
     */
    sa?: LottieValueModel.TJson;

    /**
     * Position / Translation
     */
    p?: LottiePositionModel.TJson | LottieSplitVectorModel.IJson;

    /**
     * Rotation in degrees, clockwise
     */
    r?: LottieValueModel.TJson;

    /**
     * Split rotation component (X)
     */
    rx?: LottieValueModel.TJson;

    /**
     * Split rotation component (Y)
     */
    ry?: LottieValueModel.TJson;

    /**
     * Split rotation component (Z), equivalent to `r` when not split
     */
    rz?: LottieValueModel.TJson;

    /**
     * Orientation
     */
    or?: LottieMultiDimensionalModel.TJson;
  }
}
/* v8 ignore stop */
